import React, { useState } from 'react';
import { FaEye, FaTrash } from 'react-icons/fa';
import { BUCKET_LABELS } from './bucketLabels';

export default function ResearchResultCard({
  item,
  portfolioId,
  apiBaseUrl: apiBaseUrlProp,
  authToken,
  onView,
  onDelete,
}) {
  const apiBaseUrl = apiBaseUrlProp || window.location.origin.replace('/web', '');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const truncateMarkdown = (markdown, maxLength = 150) => {
    // Strip markdown syntax
    const stripped = markdown.replace(/[#*`_\[\]]/g, '');
    if (stripped.length <= maxLength) return stripped;
    return stripped.substring(0, maxLength - 3) + '...';
  };

  const formatDateTime = (dateStr) => {
    const date = new Date(dateStr.endsWith('Z') ? dateStr : dateStr + 'Z');
    const now = new Date();
    const diffMs = now - date;
    const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffHrs < 1) {
      return 'Just now';
    } else if (diffHrs < 24) {
      return `${diffHrs}h ago`;
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const formatExpiry = (expiresAt) => {
    const now = new Date();
    const expiry = new Date(expiresAt.endsWith('Z') ? expiresAt : expiresAt + 'Z');
    const diffMs = expiry - now;
    const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMs <= 0) {
      return { text: 'Expired', type: 'expired' };
    } else if (diffHrs < 24) {
      return { text: `Expires in ${diffHrs}h`, type: 'warning' };
    } else if (diffDays < 7) {
      return { text: `Expires in ${diffDays}d`, type: 'normal' };
    } else {
      return { text: `Expires in ${diffDays}d`, type: 'normal' };
    }
  };

  const expiryInfo = formatExpiry(item.expires_at);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const pid = portfolioId || item.portfolio_id;
      const headers = {};
      if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
      const response = await fetch(
        `${apiBaseUrl}/portfolio/${pid}/research/results/${item.id}`,
        { method: 'DELETE', headers }
      );

      if (!response.ok) {
        throw new Error('Failed to delete result');
      }

      onDelete(item.id);
    } catch (err) {
      console.error('Failed to delete research result:', err);
      alert(`Failed to delete: ${err.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const visibleTokens = item.proposed_tokens.slice(0, 4);
  const remainingCount = item.proposed_tokens.length - 4;

  return (
    <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5">
      {/* Header row */}
      <div className="flex items-center justify-between mb-3">
        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-900/40 border border-cyan-700/50 text-cyan-300`}>
          {BUCKET_LABELS[item.bucket]}
        </span>
        <div className="flex items-center gap-3 text-xs">
          <span className="text-slate-400">{formatDateTime(item.created_at)}</span>
          <span className={expiryInfo.type === 'warning' ? 'text-amber-400' : expiryInfo.type === 'expired' ? 'text-red-400' : 'text-slate-400'}>
            {expiryInfo.text}
          </span>
        </div>
      </div>

      {/* Token chips */}
      <div className="mb-3">
        <div className="flex items-center gap-1.5 flex-wrap">
          {visibleTokens.map((token, idx) => (
            <span
              key={idx}
              className="px-2 py-0.5 bg-slate-700/50 border border-slate-600 text-slate-300 text-xs font-medium rounded"
            >
              {token}
            </span>
          ))}
          {remainingCount > 0 && (
            <span className="px-2 py-0.5 bg-slate-700/30 border border-slate-600 text-slate-400 text-xs rounded">
              +{remainingCount} more
            </span>
          )}
        </div>
      </div>

      {/* Summary preview */}
      <p className="text-slate-400 text-sm mb-4" style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {truncateMarkdown(item.summary_markdown)}
      </p>

      {/* Action buttons */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => onView(item.id)}
          disabled={expiryInfo.type === 'expired'}
          className="flex items-center gap-2 px-3 py-2 text-cyan-400 hover:text-cyan-300 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <FaEye className="w-4 h-4" />
          View
        </button>
        <div className="flex items-center gap-2">
          {showDeleteConfirm ? (
            <>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                className="px-3 py-1.5 text-slate-300 hover:text-white text-sm font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded transition-colors disabled:opacity-50"
              >
                <FaTrash className="w-3 h-3" />
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </>
          ) : (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              disabled={expiryInfo.type === 'expired'}
              className="flex items-center gap-1.5 px-3 py-1.5 text-red-400 hover:text-red-300 text-sm font-medium disabled:opacity-50 transition-colors"
            >
              <FaTrash className="w-4 h-4" />
              Delete
            </button>
          )}
        </div>
      </div>

      {/* Delete confirmation dialog (inline) */}
      {showDeleteConfirm && (
        <div className="mt-3 p-3 bg-amber-900/20 border border-amber-800/50 rounded-lg">
          <p className="text-amber-300 text-sm">
            Are you sure you want to delete this research result? This action cannot be undone.
          </p>
        </div>
      )}
    </div>
  );
}
